import { SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, DateTools, Input, Select } from '@treezor/design-system';

import { InfoTooltip } from '@/ui-kit/Tips/InfoTooltip';

import { IUser } from '@/features/user/store/user.model';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { PersonalSituationSummary } from '@/features/user/components/CreateUser/PersonalSituationSummary';
import { CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { UserContentLabel } from '@/features/user/components/contentLayout';

import { TKycSection } from '@/features/kyc/components/userInformation/InformationSection';
import { useUpdateUser } from '@/features/kyc/hooks/useUpdateUser';

interface IPhysicalUserverification {
  user: IUser;
  isBirthAndNationalityOnEdition: boolean;
  isSituationOnEdition: boolean;
}

export function usePhysicalUserVerificationProps({
  user,
  isBirthAndNationalityOnEdition,
  isSituationOnEdition,
}: IPhysicalUserverification) {
  const { t } = useTranslation();
  const config = useDashboardConfig();
  const hasPersonalSituation = config?.settings?.users?.dataQuality?.includes('personalSituation');

  const {
    onUpdateUser,
    handleValueChange,
    register,
    handleInputChange,
    isLoading,
    watch,
    language,
    handleDateChange,
    onCancelEdition,
  } = useUpdateUser(user);

  const birthdayWatch = watch('birthday');
  const selectedDate = DateTools.localStringToDate(birthdayWatch, language);

  const { countryCodeOptions, monthlyIncomeRangeOptions, personalAssetRanges, occupationCategoryOptions } =
    useFieldOptions();

  const isBirthAndNationalityInvalid = useMemo(() => {
    return !!selectedDate && DateTools.compare(new Date(), selectedDate) === 1;
  }, [selectedDate]);

  const birthNationality: TKycSection = useMemo(
    () => ({
      subTitle: t('pages.dashboard.createUser.form.blocks.birthNationality'),
      ...(isBirthAndNationalityOnEdition
        ? {
            component: (
              <div className="flex flex-col gap-3">
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.birthday')}</UserContentLabel>
                  <DatePicker
                    selectedDate={selectedDate}
                    label=""
                    todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
                    clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
                    onChangeDate={(date) => handleDateChange(date, 'birthday')}
                    language={language}
                    error={
                      DateTools.compare(new Date(), selectedDate) === 1
                        ? t('pages.dashboard.createUser.form.rules.invalidDate')
                        : undefined
                    }
                    size="s"
                    testId="datepicker-kyc-birthday"
                  />
                </CustomColumns>
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.birthCountry')}</UserContentLabel>
                  <Select
                    options={countryCodeOptions}
                    withSearch
                    defaultValue={user?.birthCountry}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    onChange={(value: string) => handleValueChange(value, 'birthCountry')}
                    showDelete
                    size="s"
                    testId="select-kyc-birth-country"
                  />
                </CustomColumns>
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.placeOfBirth')}</UserContentLabel>
                  <Input
                    type="text"
                    label=""
                    {...register('placeOfBirth')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'placeOfBirth')
                    }
                    size="s"
                    testId="input-kyc-birth-place"
                  />
                </CustomColumns>
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.nationality')}</UserContentLabel>
                  <Select
                    options={countryCodeOptions}
                    withSearch
                    defaultValue={user?.nationality}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    onChange={(value: string) => handleValueChange(value, 'nationality')}
                    showDelete
                    size="s"
                    testId="select-kyc-nationality"
                  />
                </CustomColumns>
                <AdditonalDataLayout
                  shouldShow={!!watch('nationalityOther')}
                  label={t('pages.dashboard.createUser.form.actions.addOtherNationality')}
                >
                  <CustomColumns columns={[1, 3]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.nationalityOther')}</UserContentLabel>
                    <Select
                      options={countryCodeOptions}
                      withSearch
                      defaultValue={user?.nationalityOther}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      onChange={(value: string) => handleValueChange(value, 'nationalityOther')}
                      showDelete
                      size="s"
                      testId="select-kyc-other-nationality"
                    />
                  </CustomColumns>
                </AdditonalDataLayout>
              </div>
            ),
          }
        : {
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.birthday'), value: user?.birthday || '/' },
                  {
                    label: t('pages.dashboard.createUser.form.birthCountry'),
                    value: user?.birthCountry ? t(`shared.countries.${user.birthCountry}`) : '/',
                  },
                  { label: t('pages.dashboard.createUser.form.placeOfBirth'), value: user.placeOfBirth || '/' },
                  {
                    label: t('pages.dashboard.createUser.form.nationalities'),
                    value:
                      [user?.nationality, user?.nationalityOther]
                        .filter(Boolean)
                        .map((nationality) => t(`shared.countries.${nationality}`))
                        .join(', ') || '/',
                  },
                ],
              },
            ],
          }),
    }),
    [
      countryCodeOptions,
      handleDateChange,
      handleInputChange,
      handleValueChange,
      isBirthAndNationalityOnEdition,
      language,
      register,
      selectedDate,
      t,
      user.birthCountry,
      user?.birthday,
      user?.nationality,
      user?.nationalityOther,
      user.placeOfBirth,
      watch,
    ],
  );

  const situation: TKycSection = useMemo(
    () => ({
      subTitle: t('pages.dashboard.createUser.form.blocks.situation'),
      ...(isSituationOnEdition
        ? {
            component: (
              <div className="flex flex-col gap-3">
                <CustomColumns columns={[2, 3]}>
                  <div className="flex items-center gap-2">
                    <UserContentLabel>{t('pages.dashboard.createUser.form.occupation.label')}</UserContentLabel>
                    <InfoTooltip
                      tooltipInfo={t('pages.dashboard.createUser.form.occupation.tooltip')}
                      infoClassName="min-w-[240px]"
                    />
                  </div>
                  <Select
                    options={occupationCategoryOptions}
                    defaultValue={user.occupationCategory}
                    onChange={(value: number) => handleValueChange(value, 'occupationCategory')}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    tooltipInfo={{
                      content: t('pages.dashboard.createUser.form.occupation.tooltip'),
                      className: 'min-w-[240px]',
                    }}
                    withSearch
                    showDelete={!hasPersonalSituation}
                    size="s"
                    testId="select-kyc-occupation-category"
                  />
                </CustomColumns>
                <CustomColumns columns={[2, 3]}>
                  <div className="flex items-center gap-2">
                    <UserContentLabel>
                      {t('pages.dashboard.createUser.form.personalAssetsEstimated.label')}
                    </UserContentLabel>
                    <InfoTooltip
                      tooltipInfo={t('pages.dashboard.createUser.form.personalAssetsEstimated.tooltip')}
                      infoClassName="min-w-[240px]"
                    />
                  </div>
                  <Select
                    options={personalAssetRanges}
                    defaultValue={user.personalAssetsRange}
                    onChange={(value: number) => handleValueChange(value, 'personalAssetsRange')}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    withSearch
                    tooltipInfo={{
                      content: t('pages.dashboard.createUser.form.personalAssetsEstimated.tooltip'),
                      className: 'min-w-[240px]',
                    }}
                    showDelete={!hasPersonalSituation}
                    size="s"
                    testId="select-kyc-personal-assets"
                  />
                </CustomColumns>
                <CustomColumns columns={[2, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.netMontlyIncome.label')}</UserContentLabel>
                  <Select
                    options={monthlyIncomeRangeOptions}
                    defaultValue={user.monthlyIncomeRange}
                    onChange={(value: number) => handleValueChange(value, 'monthlyIncomeRange')}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    withSearch
                    showDelete={!hasPersonalSituation}
                    size="s"
                    testId="select-kyc-monthly-income-range"
                  />
                </CustomColumns>
              </div>
            ),
          }
        : {
            categories: [
              {
                ...(hasPersonalSituation
                  ? {
                      component: (
                        <PersonalSituationSummary
                          occupationCategory={user.occupationCategory}
                          monthlyIncomeRange={user.monthlyIncomeRange}
                          personalAssetsRange={user.personalAssetsRange}
                        />
                      ),
                    }
                  : {
                      items: [
                        {
                          label: t('pages.currentProfiles.profile.financial.occupationType'),
                          value: user?.occupationType
                            ? t(`pages.currentProfiles.profile.financial.occupationTypes.${user.occupationType}`)
                            : '/',
                        },
                        {
                          label: t('pages.dashboard.createUser.form.personalAssets.title'),
                          value: user?.personalAssets
                            ? t(`pages.dashboard.createUser.form.personalAssets.${user.personalAssets}`)
                            : '/',
                        },
                        {
                          label: t('pages.dashboard.createUser.form.incomeRange.title'),
                          value: user?.incomeRange
                            ? t(`pages.dashboard.createUser.form.incomeRange.${user.incomeRange}`)
                            : '/',
                        },
                      ],
                    }),
              },
            ],
          }),
    }),
    [
      handleValueChange,
      hasPersonalSituation,
      isSituationOnEdition,
      monthlyIncomeRangeOptions,
      occupationCategoryOptions,
      personalAssetRanges,
      t,
      user.incomeRange,
      user.monthlyIncomeRange,
      user.occupationCategory,
      user.occupationType,
      user.personalAssets,
      user.personalAssetsRange,
    ],
  );

  return {
    isBirthAndNationalityInvalid,
    birthNationality,
    situation,
    isLoading,
    onUpdateUser,
    onCancelEdition,
  };
}
